import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { AlertTriangle, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { registerSecureExportModal } from '../utils/secureExport';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthToken = () => localStorage.getItem('auth_token');

export const SecureExportModal = () => {
  const [request, setRequest] = useState(null); // { url, filename, resolve, reject }
  const [disclaimer, setDisclaimer] = useState(null); // { version, text }
  const [accepted, setAccepted] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleOpen = useCallback(({ url, filename, method, disclaimerText, resolve, reject }) => {
    setRequest({ url, filename, method: method || 'GET', disclaimerText, resolve, reject });
    setAccepted(false);
    setPassword('');
    setConfirmPassword('');
    setError('');
  }, []);

  useEffect(() => {
    registerSecureExportModal(handleOpen);
  }, [handleOpen]);

  useEffect(() => {
    if (request && !disclaimer) {
      fetch(`${API_URL}/api/export-disclaimer`, { headers: { Authorization: `Bearer ${getAuthToken()}` } })
        .then((r) => r.json())
        .then((d) => setDisclaimer(d))
        .catch(() => setDisclaimer({ version: 'v1', text: 'Estás por descargar datos personales confidenciales.' }));
    }
  }, [request, disclaimer]);

  const handleClose = () => {
    if (request?.reject) request.reject(new Error('cancelado'));
    setRequest(null);
  };

  const handleConfirm = async () => {
    setError('');
    if (!accepted) {
      setError('Tenés que aceptar el disclaimer para continuar');
      return;
    }
    if (password.length < 6) {
      setError('La clave debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las claves no coinciden');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(request.url, {
        method: request.method || 'GET',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'X-Disclaimer-Accepted': 'true',
          'X-Disclaimer-Version': disclaimer?.version || 'v1',
          'X-Export-Password': password,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Error ${res.status} al generar la descarga`);
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = request.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(
        request.filename?.toLowerCase().endsWith('.zip')
          ? 'Descarga lista. Abrila con 7-Zip o WinRAR (no con "Extraer todo" de Windows) y guardá la clave: sin ella no se recupera.'
          : 'Descarga lista. Guardá la clave: sin ella el archivo no se recupera.',
        { duration: 8000 }
      );
      request.resolve();
      setRequest(null);
    } catch (e) {
      setError(e.message || 'Error al descargar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md" data-testid="secure-export-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-600" />
            Descarga segura de datos personales
          </DialogTitle>
          <DialogDescription data-testid="secure-export-disclaimer-text">
            {request?.disclaimerText || disclaimer?.text || 'Cargando disclaimer...'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 py-1">
          <Checkbox
            id="export-disclaimer-accept"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(!!v)}
            data-testid="secure-export-disclaimer-checkbox"
          />
          <Label htmlFor="export-disclaimer-accept" className="text-sm leading-snug cursor-pointer">
            Acepto los términos de uso de esta descarga y entiendo que queda registrada.
          </Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="export-password">Clave para cifrar el archivo</Label>
          <Input
            id="export-password"
            type="password"
            placeholder="Mínimo 6 caracteres"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="secure-export-password-input"
          />
          <Input
            type="password"
            placeholder="Repetí la clave"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            data-testid="secure-export-password-confirm-input"
          />
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Si perdés esta clave, el archivo no se puede recuperar.
          </p>
        </div>

        {request?.filename?.toLowerCase().endsWith('.zip') && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="secure-export-zip-instructions">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            Este archivo es un <strong>.zip cifrado con AES-256</strong>. El "Extraer todo" nativo de Windows
            <strong> no soporta AES</strong> y va a dar error ("no se pudo crear el archivo de destino"). Abrilo con{' '}
            <strong>7-Zip</strong> o <strong>WinRAR</strong> (clic derecho → Abrir con / Extraer aquí) e ingresá la clave de arriba.
          </p>
        )}

        {error && (
          <p className="text-sm text-red-600" data-testid="secure-export-error">{error}</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading} data-testid="secure-export-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={loading} data-testid="secure-export-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            Descargar cifrado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SecureExportModal;
